import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { staffService, StaffMember } from '../services/staff';
import { locationService, Location } from '../services/location';

interface StaffModalProps {
  staff: StaffMember | null;
  currentUserId: string;
  onClose: () => void;
  onSave: () => void;
}

const StaffModal: React.FC<StaffModalProps> = ({ staff, currentUserId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'manager' as 'admin' | 'manager',
    password: '',
    confirmPassword: '',
    locationId: ''
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    locationService
      .getLocations()
      .then((all) => setLocations(all.filter((l) => l.isActive)))
      .catch((err) => console.error('Failed to load stores:', err));
  }, []);

  useEffect(() => {
    if (staff) {
      setFormData({
        email: staff.email,
        name: staff.name,
        role: staff.role,
        password: '',
        confirmPassword: '',
        locationId: staff.locationId || ''
      });
    } else {
      setFormData({
        email: '',
        name: '',
        role: 'manager',
        password: '',
        confirmPassword: '',
        locationId: ''
      });
    }
    setError('');
  }, [staff]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!staff && !formData.password) {
      setError('Password is required for new staff members');
      return;
    }
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password && formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Managers must be assigned to a store; admins may be left unassigned
    // (no store = all stores / super-admin).
    if (formData.role === 'manager' && !formData.locationId) {
      setError('Managers must be assigned to a store');
      return;
    }

    const locationId = formData.locationId || null;

    try {
      setLoading(true);
      setError('');

      if (staff) {
        // Update existing staff
        const updateData: any = {
          name: formData.name,
          role: formData.role,
          locationId
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        await staffService.updateStaffMember(staff.id, updateData);
      } else {
        // Create new staff
        await staffService.createStaffMember({
          email: formData.email,
          name: formData.name,
          role: formData.role,
          password: formData.password,
          locationId
        });
      }

      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save staff member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              {staff ? 'Edit Staff Member' : 'Add Staff Member'}
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!staff}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100"
              />
            </div>
            
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'manager' })}
                disabled={staff?.id === currentUserId}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100"
              >
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              {staff?.id === currentUserId && (
                <p className="mt-1 text-sm text-gray-500">You cannot change your own role</p>
              )}
            </div>

            <div>
              <label htmlFor="locationId" className="block text-sm font-medium text-gray-700">
                Store
              </label>
              <select
                id="locationId"
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">All stores (admin only)</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {formData.role === 'manager'
                  ? 'Managers only see and assess customers from their store.'
                  : 'Leave as "All stores" for a super-admin who sees every store.'}
              </p>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password {staff && '(leave blank to keep current)'}
              </label>
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!staff}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required={!staff || !!formData.password}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            <div className="pt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default StaffModal;